/**
 * A concurrency gate.
 *
 * Shared, because more than one screen opens a request per row: the
 * overview's per-card counts and the Sammelprüfung's field crops. It lives
 * in `lib` rather than under one of them so the second caller does not have
 * to reach into the first screen's folder.
 *
 * The overview's status lines come from one `GET
 * /v1/templates/:id/documents/count` per card — there is no aggregate
 * endpoint — so a page of 100 forms would otherwise open 100 sockets at once
 * and the browser's own six-per-host limit would queue them in an order
 * nobody chose. Four at a time keeps the first cards filled in quickly and
 * the gateway unbothered.
 *
 * The `while` rather than an `if` matters: a release wakes one waiter as a
 * microtask, and a caller arriving synchronously in between could otherwise
 * take the slot, letting the woken task push past the limit.
 */
export function createLimiter(max: number) {
  let active = 0
  const waiting: (() => void)[] = []

  return async function run<T>(task: () => Promise<T>): Promise<T> {
    while (active >= max) {
      await new Promise<void>((resolve) => waiting.push(resolve))
    }
    active += 1
    try {
      return await task()
    } finally {
      active -= 1
      waiting.shift()?.()
    }
  }
}
